import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useAppSelector } from "@/store/store";
import {
  selectIsFirstApplication,
  selectNullableApplicationId,
} from "../store/selectors";

const timeoutSize = 500; // milliseconds

interface UXSWindow extends Window {
  UXS?: {
    addProperties: (properties: {
      applicationId: string | null;
      isFirstApplication: boolean | null;
    }) => void;
    sendEvent: (eventName: string) => void;
  };
}

declare const window: UXSWindow;

export const useUxFeedbackData = () => {
  const location = useLocation();
  const currentApplicationId = useAppSelector(selectNullableApplicationId);
  const isFirstApplication = useAppSelector(selectIsFirstApplication);
  const propertiesTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setUxFeedback = (applicationId: string | null) => {
    if (!window.UXS) {
      propertiesTimeout.current = setTimeout(
        () => setUxFeedback(applicationId),
        timeoutSize
      );
      return;
    }

    // оборачиваем внешнюю библиотеку, чтобы она не роняла нам код
    try {
      window.UXS.addProperties({ applicationId, isFirstApplication });
    } catch (e) {
      console.error("UX Feedback", e);
      // в случае ошибки попробуем снова через 3 секунды
      setUxFeedback(applicationId);
    }
  };

  useEffect(() => {
    setUxFeedback(currentApplicationId);
    return () => {
      if (propertiesTimeout.current) clearTimeout(propertiesTimeout.current);
    };
  }, [currentApplicationId, isFirstApplication]);

  const sendOpenEvent = () => {
    if (!window.UXS) {
      locationTimeout.current = setTimeout(() => sendOpenEvent(), timeoutSize);
      return;
    }

    try {
      window.UXS.sendEvent(location.pathname.substring(1));
    } catch (e) {
      console.error("UX Feedback", e);
    }
  };

  useEffect(() => {
    sendOpenEvent();
    return () => {
      if (locationTimeout.current) clearTimeout(locationTimeout.current);
    };
  }, [location.pathname]);
};
